import React, { useMemo } from 'react';
import { Box, Grid } from '@chakra-ui/react';
import { KText } from '@/ui-library/components/text';
import { DetailSection } from './DetailSection';
import { CustomFieldRenderer } from './CustomFieldRenderer';
import { CustomField } from '@/pages/requisitions/types';
import { filterCustomFieldsBySelection } from '@/pages/requisitions/utils/customFieldsFilter';

interface CustomFieldsSectionProps {
  additionalInfo: Record<string, any>;
  customFieldsData: CustomField[] | undefined;
  dateFormat: string;
  departmentId: number;
  officeIds: number[];
}

export const CustomFieldsSection: React.FC<CustomFieldsSectionProps> = ({
  additionalInfo,
  customFieldsData,
  dateFormat,
  departmentId,
  officeIds,
}) => {
  // Filter custom fields based on the requisition's department and offices
  const filteredCustomFields = useMemo(() => {
    if (!customFieldsData || customFieldsData.length === 0) {
      return [];
    }

    return filterCustomFieldsBySelection(
      customFieldsData,
      departmentId,
      officeIds
    );
  }, [customFieldsData, departmentId, officeIds]);

  // Create a map of filtered field IDs for quick lookup
  const filteredFieldIds = useMemo(() => {
    return new Set(
      filteredCustomFields.map((field) => field.form_field_id.toString())
    );
  }, [filteredCustomFields]);

  // Filter additionalInfo to only show fields that are in the filtered list
  const visibleAdditionalInfo = useMemo(() => {
    if (!additionalInfo || Object.keys(additionalInfo).length === 0) {
      return {};
    }

    return Object.entries(additionalInfo).reduce(
      (acc, [fieldId, value]) => {
        if (filteredFieldIds.has(fieldId)) {
          acc[fieldId] = value;
        }
        return acc;
      },
      {} as Record<string, any>
    );
  }, [additionalInfo, filteredFieldIds]);

  if (
    !visibleAdditionalInfo ||
    Object.keys(visibleAdditionalInfo).length === 0
  ) {
    return null;
  }

  return (
    <Box mt={2}>
      <KText fontSize="md" fontWeight="semibold" color="secondary-900" mb={3}>
        Additional Information
      </KText>
      <Grid templateColumns="repeat(1, 1fr)" gap={4}>
        {Object.entries(visibleAdditionalInfo).map(([fieldId, value]) => {
          const fieldDefinition = filteredCustomFields.find(
            (cf) => cf.form_field_id === +fieldId
          );
          const fieldLabel = fieldDefinition?.label || `Field ${fieldId}`;

          return (
            <DetailSection
              key={fieldId}
              label={fieldLabel}
              subLabel={fieldDefinition?.description}
            >
              <CustomFieldRenderer
                value={value}
                fieldDefinition={fieldDefinition}
                dateFormat={dateFormat}
              />
            </DetailSection>
          );
        })}
      </Grid>
    </Box>
  );
};
